<template>
    <v-container class="fill-height" style="display: block; padding: 0px;" fluid>

        <div class="content-container">

            <v-toolbar class="sitemap-toolbar" :class="{expand: slideOut}" dense color="#F3F1F3" light>
                <v-btn icon @click="cancelClick"><v-icon>mdi-backburger</v-icon></v-btn>
                <v-text-field class="mt-3 mr-2" v-model="$props.networkMap.network_map_name" light color="#c42111" @change="networkMapStateChange" :disabled="$props.lockSave" />
                <v-autocomplete autocomplete="off" v-model="nodeSearch" :filter="nodeSearchFunc" return-object @change="goToNode" :items="searchableNodes" item-text="label" class="mt-3" color="#c42111" clearable light prepend-inner-icon="mdi-magnify" placeholder="Node Search">
                    <template v-slot:item="data" class="node-search-item" style="background-color: #ffffff; color: #000000">
                        <v-list-item-avatar>
                            <v-icon large :color="data.item.network_category_color || '#ffffff'">{{ data.item.network_category_icon || 'mdi-radio-tower' }}</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title v-html="data.item.label"></v-list-item-title>
                            <v-list-item-subtitle v-html="data.item.site_office"></v-list-item-subtitle>
                            <v-list-item-subtitle v-html="data.item.site_location"></v-list-item-subtitle>
                        </v-list-item-content>
                    </template>
                </v-autocomplete>

                <!-- Save -->
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" icon @click="saveNetworkMapClick" :disabled="$props.lockSave || (nodes.length > allowedNodes) || $props.isSaving">
                            <v-icon v-if="!$props.isSaving">mdi-content-save</v-icon>
                            <v-icon v-if="$props.isSaving">fas fa-circle-notch fa-spin</v-icon>
                        </v-btn>
                    </template>
                    <span>Save</span>
                </v-tooltip>

                <v-divider style="min-height: 32px; height: 32px; margin-top: 8px;" vertical />

                <!-- Filter Toggle -->
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" icon @click="toggleFiltersClick" :color="filtersMode ? '#c42111' : ''"><v-icon>mdi-filter</v-icon></v-btn>
                    </template>
                    <span>Toggle Filters</span>
                </v-tooltip>

                <!-- Toggle Alarms -->
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" icon @click="toggleAlarmsClick" :color="alarmsMode ? '#c42111' : ''"><v-icon>mdi-bell-ring</v-icon></v-btn>
                    </template>
                    <span>Toggle Alarms</span>
                </v-tooltip>

                <!-- Clustering -->
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" icon @click="showClusterMethods = !showClusterMethods"><v-icon>mdi-google-circles-extended</v-icon></v-btn>
                    </template>
                    <span>Cluster</span>
                </v-tooltip>

                <div class="cluster-container" :class="{show: showClusterMethods}">

                    <div class="up-arrow">
                        <div class="inner"></div>
                    </div>

                    <div v-if="showClusterMethods" style="height: 100%">
                        <div class="cluster-method" @click="clusterNodes('locale')">
                            <v-icon x-large color="#7A797A">mdi-hexagon-multiple</v-icon>
                            <span>Locale</span>
                        </div>
                        <div class="cluster-method" @click="clusterNodes('technician')">
                            <v-icon x-large color="#7A797A">mdi-account-circle</v-icon>
                            <span>Technician</span>
                        </div>
                        <div class="cluster-method" @click="clusterNodes('site_name')">
                            <v-icon x-large color="#7A797A">mdi-alpha-s-circle</v-icon>
                            <span>Site Name</span>
                        </div>
                        <div class="cluster-method" @click="clusterNodes('network_category')">
                            <v-icon x-large color="#7A797A">mdi-radio-tower</v-icon>
                            <span>Network Category</span>
                        </div>
                        <div class="cluster-method" @click="declusterNodes()">
                            <v-icon x-large color="#7A797A">mdi-gamepad-circle</v-icon>
                            <span>Decluster</span>
                        </div>
                    </div>
                </div>

                <v-divider style="min-height: 32px; height: 32px; margin-top: 8px;" vertical />

                <!-- Undo -->
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" icon @click="undoAction" :disabled="!actionHistoryStack.length"><v-icon>mdi-undo</v-icon></v-btn>
                    </template>
                    <span>Undo</span>
                </v-tooltip>

                <!-- Redo -->
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" icon @click="redoAction" :disabled="!redoStack.length"><v-icon>mdi-redo</v-icon></v-btn>
                    </template>
                    <span>Redo</span>
                </v-tooltip>

                <v-divider style="min-height: 32px; height: 32px; margin-top: 8px;" vertical />

                <!-- Sites and Settings -->
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" icon @click="slideOut = !slideOut; showClusterMethods = false;" :color="slideOut ? '#c42111' : ''"><v-icon>mdi-dots-vertical</v-icon></v-btn>
                    </template>
                    <span>Show Sites and Settings</span>
                </v-tooltip>
            </v-toolbar>


            <div class="tab-content-container" :class="{expand: slideOut}">
                <v-tabs v-model="tab" light color="#c42111" background-color="#F3F1F3">
                    <v-tabs-slider color="#c42111"></v-tabs-slider>
                    <v-tab>Sites</v-tab>
                    <v-tab>Filters</v-tab>
                    <v-tab>Alarms</v-tab>
                    <v-tab>General</v-tab>
                </v-tabs>

                <v-tabs-items v-model="tab" light>
                    <v-tab-item>
                        <div class="slide-out-site-list" light v-if="slideOutContent == 'sitelist' || true">
                            <v-toolbar light dense flat color="#F3F1F3">
                                Sites
                                <v-spacer></v-spacer>
                                <v-text-field color="#c42111" v-model="search" append-icon="mdi-magnify" label="Site Search" single-line hide-details></v-text-field>
                            </v-toolbar>
                            <v-data-table v-model="selectedSites" light height="calc(100vh - 222px)" class="elevation-1 site-list-table" :dense="siteTableDense" :headers="headers" :items="sitesList" 
                                :search="search" item-key="site_id" sort-by="site_name" :items-per-page="250" fixed-header >
                                <template v-slot:item="{item}">
                                    <tr class="site-row" :class="{selected: item.selected, disabled: !item.selectable}" @click="siteRowClick(item)" :draggable="item.selectable" @dragstart="startDrag($event, item)" disabled>
                                        <td>{{ item.site_name }}</td>
                                        <td>{{ item.site_office }}</td>
                                        <td>{{ item.site_location }}</td>
                                        <td>{{ item.site_group_names }}</td>
                                        <td><v-icon :color="item.network_category_color">{{item.network_category_icon}}</v-icon> <span>{{item.network_category_name}}</span></td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </div>
                    </v-tab-item>
                            
                    <v-tab-item style="background-color: #F3F1F3;">

                        <div class="slide-out-settings mx-4">
                            <v-data-table v-model="selectedSiteGroups" @change="applyAlarmFilterStylingToNodes" light height="calc((100vh - 222px) / 2)" class="elevation-1 site-list-table" :dense="true" :headers="siteTagsHeaders" :items="siteGroupList" 
                                :search="siteTagSearch" item-key="site_group_id" sort-by="site_group_name" :items-per-page="50" fixed-header show-select :footer-props="siteTagsFooterProps">
                                <template v-slot:top>
                                    <v-text-field v-model="siteTagSearch" class="ml-4 mr-4" color="#c42111" placeholder="Site Tag Search" height="20" />
                                </template>
                            </v-data-table>
                        </div>

                        <div class="slide-out-settings mx-4 mt-4">
                            <v-data-table v-model="selectedSiteTagCustomGroups" light height="calc((100vh - (222px + 280px)) / 2)" class="elevation-1 site-list-table" :dense="true" :headers="siteTagCustomGroupHeaders" :items="siteTagCustomGroups" 
                                :search="''" item-key="name" sort-by="site_group_name" :items-per-page="50" fixed-header show-select hide-default-footer>

                                <template v-slot:top>
                                    <span style="display: block; text-align: center;">Site Tag Groupings
                                    <v-btn @click="showEditSiteTagCustomGroups = true" style="" icon><v-icon>mdi-playlist-edit</v-icon></v-btn></span>
                                </template>

                                <template v-slot:item.tagsCount="{ item }">
                                    <span>{{ item.siteTags.length }}</span>
                                </template>

                            </v-data-table>
                        </div>
                        
                    </v-tab-item>

                    <v-tab-item>
                        <div class="slide-out-settings">
                            <v-row class="mx-4 mt-2" no-gutters>
                                <v-col cols="12">
                                    <v-select outlined :items="alarmFilterOptions" v-model="alarmFilterSetting" label="Alarm Filter" color="#c42111" class="mr-3" style="display: inline-block; width: 560px;" @change="applyAlarmFilterStylingToNodes" />
                                    <v-switch v-model="alarmFilterActive" label="Active" color="#c42111" style="display: inline-block;" @change="applyAlarmFilterStylingToNodes" />
                                </v-col>
                                <v-col cols="12">
                                    <div class="alarm-filter-severity">
                                        <span class="settings-label">Observational</span>
                                        <v-switch height="15" v-model="alarmFilterObservational" label="No/Yes" color="#c42111" @change="applyAlarmFilterStylingToNodes" />
                                    </div>
                                    <div class="alarm-filter-severity">
                                        <span class="settings-label">Minor</span>
                                        <v-switch height="15" v-model="alarmFilterMinor" label="No/Yes" color="#c42111" @change="applyAlarmFilterStylingToNodes" />
                                    </div>
                                    <div class="alarm-filter-severity">
                                        <span class="settings-label">Major</span>
                                        <v-switch height="15" v-model="alarmFilterMajor" label="No/Yes" color="#c42111" @change="applyAlarmFilterStylingToNodes" />
                                    </div>
                                    <div class="alarm-filter-severity">
                                        <span class="settings-label">Critical</span>
                                        <v-switch height="15" v-model="alarmFilterCritical" label="No/Yes" color="#c42111" @change="applyAlarmFilterStylingToNodes" />
                                    </div>
                                </v-col>
                            </v-row>
                        </div>
                    </v-tab-item>

                    <v-tab-item>
                        <div class="slide-out-settings">
                            <v-row class="mx-4 mt-2" no-gutters>
                                <v-col cols="12">
                                    <span class="settings-label">Warn on Delete</span>
                                    <v-switch height="15" v-model="generalWarnOnDelete" label="No/Yes" color="#c42111" />
                                </v-col>
                            </v-row>
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </div>


            <div class="special-mode-container" v-if="createEdgeMode">
                <span v-if="createEdgeMode && !createEdgeModeSelectedNodes.length">Create Edge Mode: Select a node(s) first</span>
                <span v-if="createEdgeMode && createEdgeModeSelectedNodes.length">Create Edge Mode: Select a node to create an edge(s)</span>
            </div>

            <div class="alarm-actions-container" :class="{expanded: alarmActionsExpanded}">
                <v-icon class="alarm-actions-icon" large @click="alarmActionsExpanded = !alarmActionsExpanded">mdi-bell-check</v-icon>
                <div class="content" v-if="alarmActionsExpanded">
                    <v-btn class="close" color="#c42111" icon small @click="alarmActionsExpanded = false"><v-icon>mdi-arrow-top-right-thick</v-icon></v-btn>
                    <span style="color: #c42111; display: block;"> <b>{{ `${selectedNodesObjs.length} ` }}</b> sites selected</span>
                    <span style="color: #c42111; display: block;"> <b>{{ `${selectedNodeAlarms.length} ` }}</b> total alarms</span>
                    <v-btn light @click="ackAllSelectedNodeAlarms" :disabled="!selectedNodeAlarms.length" small class="white--text mr-1 mt-3" width="70px" color="#c42111">Ack All</v-btn>
                    <v-btn light @click="clearAllSelectedNodeAlarms" :disabled="!selectedNodeAlarms.length" small class="white--text ml-1 mt-3" width="85px" color="#c42111">Clear All</v-btn>
                </div>
            </div>


            <div class="network-container" @drop="dropSiteOnCanvas" @dragover="allowDrop">
                <network ref="network" :nodes="nodes" :edges="edges" :options="options"
                    
                    @drag-end="onNodeDragEnd"
                    @select-node="onNodeSelected"
                    @oncontext="rightClickHandler"
                    @hover-node="onNodeHover"
                    @blur-node="onNodeBlur"

                    @nodes-mounted="getNodeDataSet"
                    @edges-mounted="getEdgeDataSet"


                    @mousedown="rectangle_mousedown"
                    @mousemove="rectangle_mousedrag"
                    @mouseup="rectangle_mouseup"
                    @after-drawing="draw_rectangle_on_network"

                    @click="onCanvasClick"
                    @hold="clickHandler"
                    @release="clickHandler"
                >
                </network>
            </div>


            <v-btn small color="#ffffff" class="zoom-buttons in" fab @click="zoomInOut(0.25)">
                <v-icon color="#c42111">mdi-plus</v-icon>
            </v-btn>
            <v-btn small color="#ffffff" class="zoom-buttons out" fab @click="zoomInOut(-0.25)">
                <v-icon color="#c42111">mdi-minus</v-icon>
            </v-btn>
            <v-btn small color="#ffffff" class="zoom-buttons fullscreen" fab @click="toggleFullscreen">
                <v-icon color="#c42111">mdi-fullscreen</v-icon>
            </v-btn>

        </div>



        <!-- <v-menu :attach="fullscreenMode ? $store.state.fullscreenElement : '.content-container'" z-index="99999999999999" v-model="showContextMenu" style="left: 200px !important; top: 200px !important;" :position-x="500" :position-y="500" absolute offset-y light> -->
        <!-- <v-menu v-model="showContextMenu" :position-x="contextMenuX" :position-y="600" absolute offset-y light :attach="$store.state.fullscreenElement"> -->
        <v-menu v-model="showContextMenu" :position-x="contextMenuX" :position-y="contextMenuY" absolute offset-y light>
            <v-list>
                <template v-if="rightClickSelectedNodeSite">
                    <v-list-item>
                        <v-list-item-avatar><v-icon large :color="rightClickSelectedNodeSite.network_category_color || '#626262'">{{ rightClickSelectedNodeSite.network_category_icon || 'mdi-radio-tower' }}</v-icon></v-list-item-avatar>
                        <v-list-item-title style="margin-left: -15px;">{{ rightClickSelectedNodeSite.site_name }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item class="d-block">
                        <v-list-item-subtitle class="d-block">{{ `Office: ${rightClickSelectedNodeSite.site_office}` }}</v-list-item-subtitle>
                        <v-list-item-subtitle class="d-block">{{ `Location: ${rightClickSelectedNodeSite.site_location}` }}</v-list-item-subtitle>
                        <v-list-item-subtitle class="d-block mb-1">{{ `Locale: ${rightClickSelectedNodeSite.site_locale}` }}</v-list-item-subtitle>
                    </v-list-item>
                </template>
                <template v-else-if="rightClickSelectedNodeCluster">
                    <v-list-item>
                        <!-- <v-list-item-avatar><v-icon large>{{ rightClickSelectedNodeCluster.image || 'mdi-radio-tower' }}</v-icon></v-list-item-avatar> -->
                        <v-list-item-avatar><v-img :src="rightClickSelectedNodeCluster.image"></v-img></v-list-item-avatar>
                        <v-list-item-title style="margin-left: -15px;">{{ rightClickSelectedNodeCluster.label }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item class="d-block">
                        <v-list-item-subtitle class="d-block">{{ `${visJsNetwork.getNodesInCluster(rightClickSelectedNodeCluster.id).length} child nodes` }}</v-list-item-subtitle>
                    </v-list-item>
                </template>
                <v-divider />
                <!-- <v-list-item @click="selectNodeWithImmediateChildren">
                    <v-list-item-title>Select with immediate children</v-list-item-title>
                </v-list-item>
                <v-list-item @click="selectNodeWithAllChildren">
                    <v-list-item-title>Select with all children</v-list-item-title>
                </v-list-item>
                <v-list-item @click="createEdgeModeClick">
                    <v-list-item-title>Create Edge</v-list-item-title>
                </v-list-item> -->
                <v-list-item @click="viewAlarmsClick">
                    <v-list-item-title>View Alarms</v-list-item-title>
                </v-list-item>
                <v-list-item @click="editSiteClick" :disabled="!editSitePermission || lockSave" v-if="rightClickSelectedNodeSite">
                    <v-list-item-title>Edit Site</v-list-item-title>
                </v-list-item>
                <v-list-item @click="siteBookClick" v-if="rightClickSelectedNodeSite">
                    <v-list-item-title>View Site Book</v-list-item-title>
                </v-list-item>
                <v-list-item @click="deleteNodeClick" v-if="rightClickSelectedNodeSite">
                    <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
                <v-list-item @click="declusterNodes([rightClickSelectedNodeCluster])" v-if="rightClickSelectedNodeCluster">
                    <v-list-item-title>Decluster Nodes</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        


        <v-dialog v-model="showNodeAlarms" max-width="80%" width="1600px" persistent :attach="$store.state.fullscreenElement">
            <alarms v-if="showNodeAlarms" :node="nodeForViewingAlarms" @close="showNodeAlarms = false"></alarms>
        </v-dialog>

        <v-dialog v-model="showEditSite" max-width="80%" width="1600px" persistent content-class="edit-site-container">
            <sites-edit v-if="showEditSite" :site="editSiteNodeSite" :siteResources="siteResources" :lockSave="false" @save="saveEditSite" @cancel="cancelEditSite"></sites-edit>
        </v-dialog>

        <v-dialog v-model="showEditSiteTagCustomGroups" max-width="80%" width="1600px" persistent :attach="$store.state.fullscreenElement">
            <site-tag-custom-groups-edit v-if="showEditSiteTagCustomGroups" :siteTags="siteGroupList" :siteTagCustomGroups="siteTagCustomGroups" @save="saveCustomSiteTagGroups" @close="showEditSiteTagCustomGroups = false"></site-tag-custom-groups-edit>
        </v-dialog>

        <v-dialog v-model="showSitebook" max-width="80%" width="1600px" persistent :attach="$store.state.fullscreenElement" v-if="showSitebook">
            <v-card>
                <v-card-title>{{ `Site Book - ${selectedSiteSitebook.site.label}` }}</v-card-title>
                <v-card-text><issue-site-book :sitebook="selectedSiteSitebook" /></v-card-text>
                <v-divider />
                <v-card-actions><div class="flex-grow-1"></div><v-btn text @click="showSitebook = false">Close</v-btn></v-card-actions>
            </v-card>
        </v-dialog>
        

    </v-container>
</template>

<script>
import Vue from 'vue';
import { Network } from "vue-vis-network";
Vue.component('network', Network);
import "vue-vis-network/node_modules/vis-network/dist/vis-network.css";
import html2canvas from 'html2canvas';
import _ from 'lodash';
import { IconImageFuncs } from "@/components/NetworkMaps/NetworkMapFuncs";
import Alarms from '@/components/NetworkMaps/Alarms';
import SitesEdit from "@/components/Sites/SitesEdit";
import SiteTagCustomGroupsEdit from "@/components/NetworkMaps/SiteTagCustomGroupsEdit";
import IssueSiteBook from "@/components/Issues/IssueSiteBook";
export default {
    name: 'NetworkMap',
    props: ['networkMap', 'lockSave', 'isSaving'],
    components: {
        Alarms,
        SitesEdit,
        SiteTagCustomGroupsEdit,
        IssueSiteBook
    },
    data: () => ({
        visJsNetwork: null,
        nodeDataSet: null,
        edgeDataSet: null,
        tab: null,
        slideOut: false,
        slideOutContent: null,
        siteTableDense: false,
        sitesList: [],
        search: '',
        headers: [
            {text: 'Site Name', align: 'start', filterable: true, groupable: false, value: 'site_name'},
            {text: 'Office', filterable: true, groupable: false, value: 'site_office'},
            {text: 'Location', filterable: true, groupable: false, value: 'site_location'},
            {text: 'Tags', filterable: true, groupable: false, value: 'site_group_names'},
            {text: 'Network Category', filterable: true, groupable: false, value: 'network_category_name'},
        ],
        selectedSites: [],

        siteGroupList: [],
        selectedSiteGroups: [],
        siteTagsHeaders: [
            {text: 'Name', align: 'start', filterable: true, groupable: false, value: 'site_group_name'},
            {text: 'Description', align: 'start', filterable: true, groupable: false, value: 'site_group_desc'},
        ],
        siteTagSearch: '',
        siteTagsFooterProps: {
            'items-per-page-options': [50, 100, 250, 500, 100, -1]
        },

        siteTagCustomGroups: [],
        selectedSiteTagCustomGroups: [],
        siteTagCustomGroupHeaders: [
            {text: 'Name', align: 'start', filterable: true, groupable: false, value: 'name'},
            {text: 'Description', align: 'start', filterable: true, groupable: false, value: 'description'},
            {text: 'Number of Tags', align: 'start', filterable: true, groupable: false, value: 'tagsCount'},
        ],
        showEditSiteTagCustomGroups: false,
        showSitebook: false,
        selectedSiteSitebook: null,
        showClusterMethods: false,
        listOfClusterNodes: [],

        filtersMode: false,
        alarmsMode: false,
        alarmsIntervalFunc: null,
        alarmFilterOptions: [
            {text: 'Hide all normal sites', value: 0},
            {text: 'Hide all normal sites whos immediate neighbors are also normal', value: 1},
        ],
        alarmFilterSetting: 0,
        alarmFilterActive: false,
        alarmFilterObservational: true,
        alarmFilterMinor: true,
        alarmFilterMajor: true,
        alarmFilterCritical: true,
        generalWarnOnDelete: true,
        actionHistoryStack: [],
        redoStack: [],
        showNodeAlarms: false,
        nodeForViewingAlarms: null,
        siteResources: null,
        showEditSite: false,
        editSiteNodeSite: null,
        alarmActionsExpanded: false,
        selectedNodes: [],
        nodeSearch: '',
        nodes: [],
        edges: [],
        options: {
            height: 'calc(100vh - 78px)',
            width: '100%',
            autoResize: true,
            nodes: {
                borderWidth: 4, // doesnt matter when using images for nodes
                color: {
                    hover: {
                        border: '#ff0000',
                        background: 'blue'
                    },
                    highlight: {
                        border: 'gold',
                        background: 'gold'
                    }
                },
                shadow: {
                    enabled: false,
                    color: '#c42111',
                    x: 0,
                    y: 0,
                    size: 15
                },
            },
            edges: {
                color: 'lightgray',
                physics: false,
                smooth: false
            },
            physics: {
                enabled: false
            },
            interaction: {
                multiselect: true,
                hover: true,
                hoverConnectedEdges: false,
            },
            layout: {
                improvedLayout: true,
                clusterThreshold: 50,
                hierarchical: {
                    enabled: false
                }
            }
        },

        rightClickSelectedNodeCluster: null,
        rightClickSelectedNodeSite: null,
        createEdgeMode: false,
        createEdgeModeSelectedNodes: [],
        showContextMenu: false,
        contextMenuX: 0,
        contextMenuY: 0,
        NO_CLICK: 0,
        RIGHT_CLICK: 3,
        drag: false,
        DOMRect: null,
        container: null,
        keyboardMoveRate: 20,
        keyboardMoveRateShift: 5,
        cmdKeyPressed: false,
        fullscreenMode: false,

        alarmColors: null,
        isDirtyTemp: false,
    }),

    computed: {
        selectedSiteGroupIds: function() {
            let value = [];
            for(let siteGroup of this.selectedSiteGroups) {
                value.push(siteGroup.site_group_id);
            }

            for(let customSiteTagGroup of this.selectedSiteTagCustomGroups) {
                for(let siteTag of customSiteTagGroup.siteTags) {
                    value.push(siteTag.site_group_id);
                }
            }
            return [...new Set(value)]; // removes duplicates
        },

        allowedNodes: function() {
            return this.$store.state.batchConfig.webapp_networkmap || 5;
        },

        editSitePermission: function() {
            return (TELSA.checkPermission(this.$store.state.user, 'alarm/sites') && TELSA.checkPermission(this.$store.state.user, 'alarm/sitebook'));
        },

        selectedNodesObjs: function() {
            let nodeIds = [];
            for(let nodeId of this.selectedNodes) {
                if(this.visJsNetwork.isCluster(nodeId)) {
                    nodeIds = nodeIds.concat(this.visJsNetwork.getNodesInCluster(nodeId));
                }
                else {
                    nodeIds.push(nodeId);
                }
            }
            return this.nodes.filter(n => nodeIds.includes(n.id));
        },

        selectedNodeAlarms: function() {
            let selectedNodesObjs = this.selectedNodesObjs;
            let alarms = [];
            for(let node of selectedNodesObjs) {
                alarms = alarms.concat(node.alarms);
            }
            return alarms;
        },

        searchableNodes: function() {
            let self = this;
            return this.nodes.concat(this.listOfClusterNodes.filter(x => self.visJsNetwork.isCluster(x.id)));
        }
    },

    methods: {
        calculateSelectedNodes: function() {
            this.selectedNodes = this.visJsNetwork.getSelection().nodes;
        },

        getSitesList: function() {
            TELSA.loading(true);
            let route = (TELSA.apiHost + "networkmap/sites");
            return this.$http.get(route).then(response => {
                let results = response.body;
                this.sitesList = results.sites;
                this.siteResources = results.resources
                this.getSiteGroups();
            });
        },

        getSiteGroups: function() {
            let route = (TELSA.apiHost + "sitegroups/list");
            return this.$http.get(route).then(response => {
                let results = response.body;
                this.siteGroupList = results;
                this.getSiteTagCustomGroups();
            });
        },

        getSiteTagCustomGroups: function() {
            let route = (`${TELSA.apiHost}networkmap/customtaggroups/${this.$props.networkMap.network_map_id}`);
            return this.$http.get(route).then(response => {
                let results = response.body;
                this.siteTagCustomGroups = results;
                this.loadNetworkMap();
            });
        },

        loadNetworkMap: function() {
            let networkMapData = this.$props.networkMap.network_map_data;

            if(!networkMapData) {
                TELSA.loading(false);
            }
            else {
                for(let nodeId in networkMapData.nodes) {
                    let site = this.sitesList.find(s => s.id == nodeId);
                    site.x = networkMapData.nodes[nodeId].x;
                    site.y = networkMapData.nodes[nodeId].y;
                    site.image = IconImageFuncs.buildIconImage(site.network_category_icon, site.network_category_color, "");
                    site.selected = false;
                    site.selectable = false;
                    this.nodes.push(site);
                }
                this.edges = networkMapData.edges;
            }
            this.createAlarmSubscription();
            TELSA.loading(false);
        },

        saveNetworkMapClick: function() {
            this.$emit('setIsSaving', true);
            setTimeout(() => {
                let networkMapData = {};
                networkMapData.nodes = this.visJsNetwork.getPositions();
                networkMapData.edges = this.edges;

                let self = this;
                html2canvas(document.getElementsByClassName('network-container')[0]).then(function(canvas) {
                    let thumbnail = canvas.toDataURL('image/png', 1);
                    self.$emit('save', JSON.stringify(networkMapData), thumbnail);
                });
            }, 200);
        },

        cancelClick: function() {
            if(this.$store.state.isDirty) {
                this.$store.commit('showConfirmModal', {
                    title: 'Are you sure?',
                    message: 'Are you sure you want to abandon your changes without saving?',
                    type: 'warning',
                    confirmAction: () => {this.$emit('cancel');}
                });
            }
            else {
                this.$emit('cancel');
            }
        },

        saveCustomSiteTagGroups: function(siteTagCustomGroupsClone) {
            TELSA.loading(true);
            let route = (TELSA.apiHost + "networkmap/customtaggroups");
            return this.$http.post(route, {args: {networkMapId: this.$props.networkMap.network_map_id, siteTagCustomGroups: siteTagCustomGroupsClone}}).then(response => {
                let results = response.body;
                this.siteTagCustomGroups = siteTagCustomGroupsClone;
                // this takes care of removing selected groups that we just deleted.
                this.selectedSiteTagCustomGroups = this.selectedSiteTagCustomGroups.filter(sg => {
                    return (siteTagCustomGroupsClone.some(g => g.name == sg.name));
                });
                // now we need to take care of the selected groups that have been updated
                for(let selectedGroup of this.selectedSiteTagCustomGroups) {
                    for(let group of siteTagCustomGroupsClone) {
                        if(selectedGroup.name == group.name) {
                            selectedGroup.siteTags = group.siteTags;
                        }
                    }
                }
                this.showEditSiteTagCustomGroups = false;
                TELSA.loading(false);
            });
        },

        showSiteListClick: function() {
            if(this.slideOutContent == 'sitelist') {
                this.slideOutContent = null;
                return;
            }
            this.slideOutContent = 'sitelist';
        },

        toggleFiltersClick: function() {
            this.filtersMode = !this.filtersMode;
            this.applyAlarmFilterStylingToNodes();
        },

        toggleAlarmsClick: function() {
            this.alarmsMode = !this.alarmsMode;
            this.$nextTick(function() {
                this.applyAlarmFilterStylingToNodes();
            });
        },

        createAlarmSubscription: function() {
            let self = this;
            this.alarmsIntervalFunc = setInterval(function() {
                self.applyAlarmsToSites();
            }, 5000);
            this.applyAlarmsToSites();
        },

        applyAlarmsToSites: function() {
            // Clear alarms list for each site so we dont continue to build list producing duplicates
            this.sitesList.forEach(s => {s.alarms = [];});

            for(let alarm of this.$store.state.alarmsList) {
                for(let site of this.sitesList) {
                    if(alarm.site_id == site.site_id) {
                        site.alarms.push(alarm);
                        break;
                    }
                }
            }

            // If they are currently viewing alarms for a clustered node then we need to
            if(this.showNodeAlarms && this.visJsNetwork.isCluster(this.nodeForViewingAlarms.id)) {
                this.nodeForViewingAlarms.alarms = this.getAlarmsForClusterNode(this.nodeForViewingAlarms.id);
                if(this.nodeForViewingAlarms.triggerChange) {
                    Vue.delete(this.nodeForViewingAlarms, 'triggerChange');
                }
                else {
                    Vue.set(this.nodeForViewingAlarms, 'triggerChange', '-');
                }
            }

            this.$nextTick(function() {
                this.applyAlarmFilterStylingToNodes();
            });
        },

        applyAlarmFilterStylingToNodes: function() {
            let dNode, image, size, hiddenByAlarm, hiddenByFilter, hiddenNodeInCluster, alarmColor = null;
            let nodeIdsThatHaveAlarms = this.nodes.filter(n => n.alarms.length).map(n => n.id);
            let dNodes = [];
            for(let node of this.nodes) {
                dNode = this.nodeDataSet.get(node.id);
                image = IconImageFuncs.buildIconImage(dNode.network_category_icon, dNode.network_category_color, "");
                size = 25;
                hiddenByAlarm = false;
                hiddenByFilter = false;
                hiddenNodeInCluster = (this.visJsNetwork.findNode(node.id).length > 1);

                if(this.alarmsMode && !hiddenNodeInCluster) {
                    alarmColor = this.getNodeColorsFromAlarms(node.alarms);
                    if(!alarmColor) {
                        hiddenByAlarm = true;
                        alarmColor = {color: '#000000', strokeColor: '#000000'};
                    }
                    else if(node.alarms.length || !this.alarmFilterActive) {
                        hiddenByAlarm = false;
                    }
                    else if(this.alarmFilterSetting === 0) {
                        hiddenByAlarm = true;
                    }
                    else if(this.alarmFilterSetting === 1) {
                        let myEdges = this.edges.filter(e => {return (e.from == node.id || e.to == node.id);});
                        if(myEdges.some(e => (nodeIdsThatHaveAlarms.includes(e.from) || nodeIdsThatHaveAlarms.includes(e.to)))) {
                            hiddenByAlarm = false;
                        }
                        else {
                            hiddenByAlarm = true;
                        }
                    }

                    image = IconImageFuncs.buildIconImage(dNode.network_category_icon, alarmColor.color, alarmColor.strokeColor);
                    size = alarmColor.size;
                }

                // If they got filters turned on and we're not already hiding the node from alarm settings, then we need to check if the filters should hide the node.
                if(this.filtersMode && !hiddenByAlarm && !hiddenNodeInCluster) {
                    if(this.selectedSiteGroupIds.length) {
                        if(node.site_groups_ids.some(id => this.selectedSiteGroupIds.includes(id))) {
                            hiddenByFilter = false;
                        }
                        else {
                            hiddenByFilter = true;
                        }
                    }
                    else {
                        hiddenByFilter = false;
                    }
                }
                
                // set/apply all the stuff to the node.
                dNode.image = image;
                dNode.size = size;
                dNode.hidden = (hiddenByAlarm || hiddenByFilter || hiddenNodeInCluster);
                node.image = image;
                node.size = size;
                node.hidden = (hiddenByAlarm || hiddenByFilter || hiddenNodeInCluster);
                dNodes.push(dNode); // This greatly speeds up the network map pt-1
            }
            this.nodeDataSet.update(dNodes); // This greatly speeds up the network map pt-2

            for(let clusterNode of this.listOfClusterNodes) {
                if(this.visJsNetwork.isCluster(clusterNode.id)) {
                    if(this.alarmsMode) {
                        let childNodes = this.visJsNetwork.getNodesInCluster(clusterNode.id);
                        let clusterAlarms = [];
                        for(let childNode of childNodes) {
                            let nodeObj = this.nodes.find(n => n.id == childNode);
                            clusterAlarms = clusterAlarms.concat(nodeObj.alarms);
                        }
                        let mostSevereAlarm = this.getNodeColorsFromAlarms(clusterAlarms);
                        if(clusterNode.shape == 'circularImage') {
                            this.visJsNetwork.updateClusteredNode(clusterNode.id, {borderWidth: 5, color: {border: mostSevereAlarm.color, highlight: {border: mostSevereAlarm.color}, hover: {border: mostSevereAlarm.color}}});
                        }
                        else {
                            let icon = IconImageFuncs.buildIconImage(clusterNode.clusterIcon, mostSevereAlarm.color, "");
                            this.visJsNetwork.updateClusteredNode(clusterNode.id, {image: icon});
                        }
                    }
                    else {
                        // Not alarms mode so set the icons back to their original color and style.
                        if(clusterNode.shape == 'circularImage') {
                            this.visJsNetwork.updateClusteredNode(clusterNode.id, {borderWidth: 1, color: {border: '#2B7CE9', highlight: {border: '#2B7CE9'}, hover: {border: '#2B7CE9'}}});
                        }
                        else {
                            let icon = IconImageFuncs.buildIconImage(clusterNode.clusterIcon, clusterNode.clusterIconColor, "");
                            this.visJsNetwork.updateClusteredNode(clusterNode.id, {image: icon});
                        }
                    }
                }
            }
        },

        getNodeColorsFromAlarms: function(alarms) {
            if(!alarms.length) {return {color: this.alarmColors.normal.background, strokeColor: '', size: 25};}
            if(this.alarmFilterCritical && alarms.some(a => a.alarm_severity.toLowerCase() == 'critical')) {return {color: this.alarmColors.critical.background, strokeColor: '', size: 35};}
            else if(this.alarmFilterMajor && alarms.some(a => a.alarm_severity.toLowerCase() == 'major')) {return {color: this.alarmColors.major.background, strokeColor: '', size: 35};}
            else if(this.alarmFilterMinor && alarms.some(a => a.alarm_severity.toLowerCase() == 'minor')) {return {color: this.alarmColors.minor.background, strokeColor: '', size: 35};}
            else if(this.alarmFilterObservational && alarms.some(a => a.alarm_severity.toLowerCase() == 'observational')) {return {color: this.alarmColors.observational.background, strokeColor: '', size: 35};}
            else if(alarms.some(a => a.alarm_severity.toLowerCase() == 'indeterminate')) {return {color: '#ffffff', strokeColor: '#000000', size: 35};}
            return null;
        },

        ackAllSelectedNodeAlarms: function() {
            this.$store.commit('showConfirmModal', {
                title: 'Delete Item',
                message: `Are you sure you want to ack all <b>${this.selectedNodeAlarms.length}</b> alarms for <b>${this.selectedNodes.length}</b> selected sites?`,
                type: 'warning',
                hideCancel: false,
                confirmAction: () => {
                    let route = (TELSA.apiHost + "alarms/acknowledge");
                    TELSA.loading(true);
                    let alarmIDs = this.selectedNodeAlarms.map(r => r.alarm_id);
                    return this.$http.post(route, {args: {alarmIDs: alarmIDs}}).then(response => {
                        let failed = response.body;
                        TELSA.loading(false);
                        if(failed) {
                            this.$store.commit('insufficientPermissions', {message: `You do not have permission to <b>acknowledge</b> the following alarms: </br></br> • ${failed}`});
                        }
                    });
                }
            });
        },

        clearAllSelectedNodeAlarms: function() {
            this.$store.commit('showConfirmModal', {
                title: 'Delete Item',
                message: `Are you sure you want to clear all <b>${this.selectedNodeAlarms.length}</b> alarms for <b>${this.selectedNodes.length}</b> selected sites?`,
                type: 'warning',
                hideCancel: false,
                confirmAction: () => {
                    let route = (TELSA.apiHost + "alarms/clear");
                    TELSA.loading(true);
                    return this.$http.post(route, {args: {alarms: this.selectedNodeAlarms}}).then(response => {
                        let failed = response.body;
                        TELSA.loading(false);
                        if(failed) {
                            this.$store.commit('insufficientPermissions', {message: `You do not have permission to <b>clear</b> the following alarms: </br></br> • ${failed}`});
                        }
                    });
                }
            });
        },

        declusterNodes: function(clusterNodes) {
            let releaseFunction = function(clusterPosition, containedNodesPositions) {
                // This puts the nodes back to their original positions
                return containedNodesPositions;
            };
            // If they didn't specify which clusters to decluster, then do all of them.
            clusterNodes = clusterNodes || this.listOfClusterNodes;
            let childNodes = []
            for(let clusterNode of clusterNodes) {
                if(this.visJsNetwork.isCluster(clusterNode.id)) {
                    childNodes = childNodes.concat(this.visJsNetwork.getNodesInCluster(clusterNode.id));
                    this.visJsNetwork.openCluster(clusterNode.id, {releaseFunction: releaseFunction});
                }
            }
            this.listOfClusterNodes = this.listOfClusterNodes.filter(c => !clusterNodes.includes(c));
            this.showClusterMethods = false;
            // Remove the 'hidden' prop to all the declustered child nodes
            let dNodes = this.nodeDataSet.get(childNodes);
            dNodes.forEach(d => {d.hidden = false});
            this.nodeDataSet.update(dNodes);

            if(this.alarmsMode) {
                this.applyAlarmFilterStylingToNodes();
            }
        },

        processPropertiesForCluster: function(clusterOptions, childNodes, childEdges) {
            // Build the cluset node X,Y from the averages of child nodes X and Y
            let xSum = 0;
            let ySum = 0;
            for(let childNode of childNodes) {
                xSum = xSum + childNode.x;
                ySum = ySum + childNode.y;
            }
            xSum = xSum / childNodes.length;
            ySum = ySum / childNodes.length;
            clusterOptions.x = xSum / childNodes.length;
            clusterOptions.y = ySum / childNodes.length;

            let baseSize = 100;
            clusterOptions.size = baseSize + (Math.floor(childNodes.length / 10) * 10);
            let baseFontSize = 22;
            clusterOptions.font.size = baseFontSize + (Math.floor(childNodes.length / 10) * 1);

            return clusterOptions;
        },

        clusterNodes: function(type) {
            let self = this;
            let defaultColor = '#626262';

            // First clear any clustering that may already be active
            if(this.listOfClusterNodes.length) {
                this.declusterNodes();
            }

            if(type == 'locale') {
                let distinctLocales = this.siteResources.distinct_office_locales.map(x => x.site_locale);
                let localeImage = IconImageFuncs.buildIconImage('mdi-hexagon-multiple', defaultColor, "");
                for(let locale of distinctLocales) {
                    let clusterOptionsByData = {
                        joinCondition: function (childOptions) {
                            return childOptions.site_locale == locale;
                        },
                        processProperties: function (clusterOptions, childNodes, childEdges) {
                            return self.processPropertiesForCluster(clusterOptions, childNodes, childEdges);
                        },
                        clusterNodeProperties: {
                            id: "cluster:locale:" + locale,
                            borderWidth: 1,
                            shape: "image",
                            image: localeImage,
                            size: 100,
                            font: {
                                size: 22
                            },
                            label: `[Locale] ${locale}`,
                            clusterIcon: 'mdi-hexagon-multiple',
                            clusterIconColor: defaultColor,
                            allowSingleNodeCluster: true
                        },
                        clusterEdgeProperties: {
                            width: 1
                        }
                    };
                    this.visJsNetwork.cluster(clusterOptionsByData);
                    this.listOfClusterNodes.push(clusterOptionsByData.clusterNodeProperties);
                }
            }
            else if(type == 'technician') {
                for(let user of this.siteResources.users_list) {
                    let technicianImage = IconImageFuncs.buildIconImage('mdi-account-circle', defaultColor, "");
                    let technicianShape = 'image';
                    if(user.user_id == this.$store.state.user.user_id) {
                        // This user is me so get image in correct spot
                        if(this.$store.state.userImage) {
                            technicianImage = this.$store.state.userImage;
                            technicianShape = 'circularImage';
                        }
                    }
                    else {
                        let userObj = this.$store.state.batchConfig.users.find(u => u.user_id == user.user_id);
                        if(userObj.userImage) {
                            technicianImage = userObj.userImage;
                            technicianShape = 'circularImage';
                        }
                    }
                    let clusterOptionsByData = {
                        joinCondition: function (childOptions) {
                            return childOptions.user_id == user.user_id;
                        },
                        processProperties: function (clusterOptions, childNodes, childEdges) {
                            return self.processPropertiesForCluster(clusterOptions, childNodes, childEdges);
                        },
                        clusterNodeProperties: {
                            id: "cluster:technician:" + user.user_id,
                            borderWidth: 1,
                            shape: technicianShape,
                            image: technicianImage,
                            size: 100,
                            font: {
                                size: 22
                            },
                            label: `[Technician] ${user.user_fullname}`,
                            clusterIcon: 'mdi-account-circle',
                            clusterIconColor: defaultColor,
                            allowSingleNodeCluster: true
                        },
                        clusterEdgeProperties: {
                            width: 1
                        }
                    };
                    this.visJsNetwork.cluster(clusterOptionsByData);
                    this.listOfClusterNodes.push(clusterOptionsByData.clusterNodeProperties);
                }
            }
            else if(type == 'site_name') {
                let distinctSiteNames = _.uniq(_.map(this.nodes, 'site_name'));
                let siteNameImage = IconImageFuncs.buildIconImage('mdi-alpha-s-circle', defaultColor, "");
                for(let siteName of distinctSiteNames) {
                    let clusterOptionsByData = {
                        joinCondition: function (childOptions) {
                            return childOptions.site_name == siteName;
                        },
                        processProperties: function (clusterOptions, childNodes, childEdges) {
                            return self.processPropertiesForCluster(clusterOptions, childNodes, childEdges);
                        },
                        clusterNodeProperties: {
                            id: "cluster:siteName:" + siteName,
                            borderWidth: 1,
                            shape: "image",
                            image: siteNameImage,
                            size: 100,
                            font: {
                                size: 22
                            },
                            label: `[Site Name] ${siteName}`,
                            clusterIcon: 'mdi-alpha-s-circle',
                            clusterIconColor: defaultColor,
                            allowSingleNodeCluster: true
                        },
                        clusterEdgeProperties: {
                            width: 1
                        }
                    };
                    this.visJsNetwork.cluster(clusterOptionsByData);
                    this.listOfClusterNodes.push(clusterOptionsByData.clusterNodeProperties);
                }
            }
            else if(type == 'network_category') {
                for(let network_category of this.siteResources.network_categories) {
                    let clusterOptionsByData = {
                        joinCondition: function (childOptions) {
                            return childOptions.network_category_id == network_category.network_category_id;
                        },
                        processProperties: function (clusterOptions, childNodes, childEdges) {
                            return self.processPropertiesForCluster(clusterOptions, childNodes, childEdges);
                        },
                        clusterNodeProperties: {
                            id: "cluster:networkCategory:" + network_category.network_category_id,
                            borderWidth: 1,
                            shape: "image",
                            image: IconImageFuncs.buildIconImage(network_category.network_category_icon, network_category.network_category_color, ""),
                            size: 100,
                            font: {
                                size: 22
                            },
                            label: `[Network Category] ${network_category.network_category_name}`,
                            clusterIcon: network_category.network_category_icon,
                            clusterIconColor: network_category.network_category_color,
                            allowSingleNodeCluster: true
                        },
                        clusterEdgeProperties: {
                            width: 1
                        }
                    };
                    this.visJsNetwork.cluster(clusterOptionsByData);
                    this.listOfClusterNodes.push(clusterOptionsByData.clusterNodeProperties);
                }
            }
            this.showClusterMethods = false;
            // Apply the 'hidden' prop to all the clustered child nodes
            let childNodes = [];
            for(let cluster of this.listOfClusterNodes) {
                if(this.visJsNetwork.isCluster(cluster.id)) {
                    childNodes = childNodes.concat(this.visJsNetwork.getNodesInCluster(cluster.id));
                    let coords = this.visJsNetwork.getPositions(cluster.id);
                    cluster.x = coords[cluster.id].x;
                    cluster.y = coords[cluster.id].y;
                }
            }
            let dNodes = this.nodeDataSet.get(childNodes);
            dNodes.forEach(d => {d.hidden = true});
            this.nodeDataSet.update(dNodes);

            if(this.alarmsMode) {
                this.applyAlarmFilterStylingToNodes();
            }

            this.networkMapStateChange({type: 'clusteredNodes', data: type});
        },

        siteRowClick: function(site) {
            if(!site.selectable) {
                return;
            }

            if(site.selected) {
                // De-select now
                this.selectedSites.splice(this.selectedSites.indexOf(site), 1);
                site.selected = false;
            }
            else {
                site.selected = true;
                this.selectedSites.push(site);
            }
        },

        onNodeHover: function() {
            if(this.createEdgeMode && this.createEdgeModeSelectedNodes.length) {
                this.visJsNetwork.network.canvas.body.container.style.cursor = 'pointer';
            }
        },
        onNodeBlur: function() {
            this.visJsNetwork.network.canvas.body.container.style.cursor = this.selectedSites.length ? 'copy' : 'move';
        },

        clickHandler: function(params) {
            //console.log('GENERAL CLICK HANDLER. DOESNT CURRENTLY DO ANYTHING. PARAMS: ', params);
        },

        onCanvasClick: function(params) {
            // Handle adding new sites to the canvas
            if(this.selectedSites.length) {
                let add_x_pos = params.pointer.canvas.x;
                let add_y_pos = params.pointer.canvas.y;
                for(let [idx, site] of this.selectedSites.entries()) {
                    site.x = (add_x_pos + (idx * 60));
                    site.y = (add_y_pos);
                    site.image = IconImageFuncs.buildIconImage(site.network_category_icon, site.network_category_color, "");
                    this.nodes.push(site);
                    site.selected = false;
                    site.selectable = false;
                }
                this.networkMapStateChange({type: 'addSites', data: this.selectedSites});
                this.selectedSites = [];
            }
            this.calculateSelectedNodes();
        },

        onNodeSelected: function(params) {
            let node_id = params.nodes[0];

            // Check if we're in create edge mode and we have selected node(s).
            if(this.createEdgeMode && this.createEdgeModeSelectedNodes.length && !this.visJsNetwork.isCluster(node_id)) {
                let edges = [];
                for(let childNode of this.createEdgeModeSelectedNodes) {
                    if(!this.visJsNetwork.isCluster(childNode)) {
                        this.edges.push({from: node_id, to: childNode});
                        edges.push({from: node_id, to: childNode});
                    }
                }
                this.createEdgeModeSelectedNodes = [];
                this.visJsNetwork.unselectAll();
                if(this.edges.length) {
                    this.networkMapStateChange({type: 'addEdges', data: edges});
                }
                return;
            }

            // Handle adding new sites is children nodes and build the connections(edges)
            if(this.selectedSites.length) {
                let add_x_pos = params.pointer.canvas.x;
                let add_y_pos = params.pointer.canvas.y;
                let x_direction = 0;
                let x_multiple = 0;
                for(let [idx, site] of this.selectedSites.entries()) {
                    site.x = (add_x_pos + ((x_multiple * 80) * x_direction));
                    x_multiple = idx <= 1 ? 1 : Math.floor(idx / 2) + 1;
                    x_direction = ((idx % 2) ? -1 : 1);
                    site.y = (add_y_pos + 100);
                    site.image = IconImageFuncs.buildIconImage(site.network_category_icon, site.network_category_color, "");
                    this.nodes.push(site);
                    this.edges.push({from: node_id, to: site.site_id});
                    site.selected = false;
                    site.selectable = false;
                }
                this.networkMapStateChange({type: 'addSites', data: this.selectedSites});
                this.selectedSites = [];
                this.visJsNetwork.unselectAll();
            }
            this.calculateSelectedNodes();
        },

        onNodeDragEnd: function(params) {
            if(!params.nodes.length) {
                // They weren't dragging a node but rather the whole canvas
                return;
            }
            let oldNodesPositions = [];
            for(let nodeId of params.nodes) {
                if(this.visJsNetwork.isCluster(nodeId)) {
                    let clusterNode = this.listOfClusterNodes.find(n => n.id == nodeId);
                    oldNodesPositions.push({nodeId: nodeId, x: clusterNode.x, y: clusterNode.y});
                    let clusterNodePosition = this.visJsNetwork.getPositions(nodeId);
                    clusterNode.x = clusterNodePosition[nodeId].x;
                    clusterNode.y = clusterNodePosition[nodeId].y;
                    this.visJsNetwork.updateClusteredNode(nodeId, {x: clusterNodePosition[nodeId].x, y: clusterNodePosition[nodeId].y});
                }
                else {
                    let node = this.nodes.find(n => {return nodeId == n.id});
                    oldNodesPositions.push({nodeId: nodeId, x: node.x, y: node.y});
                    let nodePosition = this.visJsNetwork.getPositions(nodeId);
                    node.x = nodePosition[nodeId].x;
                    node.y = nodePosition[nodeId].y;
                    this.nodeDataSet.update(node);
                }
            }
            this.networkMapStateChange({type: 'dragNodes', data: oldNodesPositions});
        },


        // Functions for dragging a site onto the canvas
        startDrag: function(event, item) {
            event.dataTransfer.dropEffect = 'move';
            event.dataTransfer.effectAllowed = 'move';
            let img = document.createElement('img');
            img.src = IconImageFuncs.buildIconImage(item.network_category_icon, item.network_category_color, "");
            event.dataTransfer.setData('site_id', item.site_id);
            event.dataTransfer.setData('image', img.src);
            event.dataTransfer.setDragImage(img, 10, 10);
        },

        allowDrop: function(event) {
            event.preventDefault();
        },

        dropSiteOnCanvas: function(event) {
            let site_id = event.dataTransfer.getData('site_id');
            let site = this.sitesList.find(s => {return s.site_id == site_id});

            if(site) {
                let canvasCoordinates = this.visJsNetwork.domToCanvas({x: event.layerX, y: event.layerY});

                // Check if we dragged the site onto a node. If yes then make it a child. 
                let node = this.visJsNetwork.getNodeAt({x: event.layerX, y: event.layerY});

                site.x = canvasCoordinates.x;
                site.y = canvasCoordinates.y;
                site.selected = false;
                site.selectable = false;
                site.image = event.dataTransfer.getData('image');
                this.nodes.push(site);
                if(node) {
                    site.y = (site.y + 100);
                    this.edges.push({from: node, to: site.site_id});
                }
                
                // If they had other sites selected, then de-select them now. We only allow dragging 1 site onto the canvas at a time. 
                for(let site of this.selectedSites) {
                    site.selected = false;
                }
                this.selectedSites = [];
                this.networkMapStateChange({type: 'addSites', data: [site]});
            }
        },


        // Functions for searching and focusing on a node on the canvas
        nodeSearchFunc: function(item, queryText, itemText) {
            let siteName = item.site_name ? item.site_name.toLowerCase() : "";
            let siteOffice = item.site_office ? item.site_office.toLowerCase() : "";
            let siteLocation = item.site_location ? item.site_location.toLowerCase() : "";
            let nodeLabel = item.label ? item.label.toLowerCase() : "";
            let searchText = queryText.toLowerCase();
            return (siteName.indexOf(searchText) > -1) || (siteOffice.indexOf(searchText) > -1) || (siteLocation.indexOf(searchText) > -1) || (nodeLabel.indexOf(searchText) > -1);
        },

        goToNode: function() {
            if(this.nodeSearch) {
                let nodeOrClusterId = this.visJsNetwork.findNode(this.nodeSearch.id)[0];
                this.visJsNetwork.focus(nodeOrClusterId, {
                    animation: true,
                    scale: 1.5
                });
            }
        },


        // Right click functions
        rightClickHandler: function(params) {
            params.event.preventDefault(); // Prevent the native browser context menu from showing.
            let node = this.visJsNetwork.getNodeAt(params.pointer.DOM);
            // If they didn't right-click on a node then dont show anything.
            if(!node) {
                this.showContextMenu = false;
                this.rightClickSelectedNodeSite = null;
                this.rightClickSelectedNodeCluster = null;
                return;
            }

            if(this.visJsNetwork.isCluster(node)) {
                // They right clicked on a cluster node
                this.rightClickSelectedNodeSite = null;
                this.rightClickSelectedNodeCluster = this.listOfClusterNodes.find(c => c.id == node);
            }
            else {
                // They right clicked on a regular site node
                this.visJsNetwork.selectNodes([node]);
                this.rightClickSelectedNodeCluster = null;
                this.rightClickSelectedNodeSite = this.nodes.find(n => n.id == node);
            }
            this.showContextMenu = true;
            this.contextMenuX = params.event.pageX;
            this.contextMenuY = params.event.pageY;
        },

        selectNodeWithImmediateChildren: function(event) {
            let parentNode = this.visJsNetwork.getSelectedNodes();
            let connectingNodes = this.visJsNetwork.getConnectedNodes(parentNode, 'to');
            this.visJsNetwork.selectNodes(parentNode.concat(connectingNodes));
        },

        selectNodeWithAllChildren: function(event) {
            let parentNode = this.visJsNetwork.getSelectedNodes();
            let allChildrenNodes = [];
            let self = this;
            let getChildrenRecursive = function(parent) {
                let children = self.visJsNetwork.getConnectedNodes(parent, 'to');
                if(children.length) {
                    allChildrenNodes = allChildrenNodes.concat(children);
                    for(let child of children) {
                        getChildrenRecursive(child);
                    }
                }
            }
            getChildrenRecursive(parentNode);
            this.visJsNetwork.selectNodes(parentNode.concat(allChildrenNodes));
        },

        createEdgeModeClick: function() {
            // let selectedNode = this.visJsNetwork.getSelectedNodes()[0];
            // if(this.createEdgeMode == selectedNode) {
            //     this.createEdgeMode = null;
            // }
            // else {
            //     this.createEdgeMode = selectedNode;
            // }
        },

        deleteNodeClick: function() {
            let nodeId = this.rightClickSelectedNodeSite.id;

            this.visJsNetwork.selectNodes([nodeId]);
            this.deleteSelected();
            return;
        },

        deleteSelected: function() {
            let selected = this.visJsNetwork.getSelection();
            if(!selected || (!selected.nodes.length && !selected.edges.length)) {
                return;
            }

            let nodeIds = selected.nodes.filter(nid => !this.visJsNetwork.isCluster(nid));
            let edgeIds = selected.edges.filter(eid => !eid.includes('clusterEdge'));
            let clusterNodeIds = selected.nodes.filter(nid => this.visJsNetwork.isCluster(nid));
            let clusterEdgeIds = selected.edges.filter(eid => eid.includes('clusterEdge'));

            for(let cid of clusterNodeIds) {
                nodeIds = nodeIds.concat(this.visJsNetwork.getNodesInCluster(cid));
            }
            for(let cid of clusterEdgeIds) {
                edgeIds = edgeIds.concat(this.visJsNetwork.getBaseEdges(cid));
            }

            let nodes = this.nodes.filter(n => nodeIds.includes(n.id));
            let edges = this.edges.filter(e => edgeIds.includes(e.id));
            let self = this;
            let confirmDelete = function() {
                self.networkMapStateChange({type: 'deleteSelected', data: {nodes: nodes, edges: edges} });
                self.deleteStuff(nodeIds, edgeIds);
            };

            if(this.generalWarnOnDelete) {
                this.$store.commit('showConfirmModal', {
                    title: 'Delete Item',
                    message: `Are you sure you want to delete ${nodeIds.length} nodes and ${edgeIds.length} edges?`,
                    type: 'delete',
                    hideCancel: false,
                    confirmAction: () => confirmDelete()
                });
            }
            else {
                //this.deleteStuff(nodeIds, edgeIds);
                confirmDelete();
            }
        },
        deleteStuff(nodeIds, edgeIds) {
            this.edges = this.edges.filter(e => !edgeIds.includes(e.id));
            this.nodes = this.nodes.filter(n => {
                if(!nodeIds.includes(n.id)) {
                    return true;
                }
                else {
                    n.selectable = true;
                    return false;
                }
            });
            this.visJsNetwork.unselectAll();
            this.networkMapStateChange();
        },

        viewAlarmsClick: function() {
            if(this.rightClickSelectedNodeSite) {
                this.nodeForViewingAlarms = this.rightClickSelectedNodeSite;
            }
            else if(this.rightClickSelectedNodeCluster) {
                let clusterAlarms = this.getAlarmsForClusterNode(this.rightClickSelectedNodeCluster.id)
                this.rightClickSelectedNodeCluster.alarms = clusterAlarms;
                this.nodeForViewingAlarms = this.rightClickSelectedNodeCluster;
            }
            this.showNodeAlarms = true;
        },

        getAlarmsForClusterNode: function(clusterNodeId) {
            let childNodes = this.visJsNetwork.getNodesInCluster(clusterNodeId);
            let clusterAlarms = [];
            for(let childNode of childNodes) {
                let nodeObj = this.nodes.find(n => n.id == childNode);
                clusterAlarms = clusterAlarms.concat(nodeObj.alarms);
            }
            return clusterAlarms;
        },

        editSiteClick: function() {
            // Get sites lock first
            let lockName = `edit_site`;
            this.$store.dispatch('getLock', lockName).then(lockOwner => {
                let secondLockName = `edit_sitebook`;
                this.$store.dispatch('getLock', secondLockName).then(secondLockOwner => {

                    if(lockOwner) {
                        // Disable save and let user know page is locked!
                        TELSA.loading(false);
                        this.$store.commit('showConfirmModal', {
                            title: 'Lock Warning',
                            message: `The sites list is already locked for editing by <font color="#FB8C00"><b>${lockOwner}</b></font>. You cannot edit this site at this time.`,
                            type: 'warning',
                            hideCancel: true,
                            confirmAction: function(){}
                        });
                    }
                    else if(secondLockOwner) {
                        // Disable save and let user know page is locked!
                        this.$store.commit('showConfirmModal', {
                            title: 'Lock Warning',
                            message: `The Site Book is already locked for editing by <font color="#FB8C00"><b>${secondLockOwner}</b></font>. You cannot save any changes at this time.`,
                            type: 'warning',
                            hideCancel: true,
                            confirmAction: function(){}
                        });
                    }
                    else {
                        // Get the site to edit.
                        let route = `${TELSA.apiHost}sites/site/${this.rightClickSelectedNodeSite.id}`;
                        TELSA.loading(true);
                        return this.$http.get(route).then(response => {
                            let result = response.body;
                            this.editSiteNodeSite = result.site;
                            this.isDirtyTemp = this.$store.state.isDirty;
                            this.showEditSite = true;
                            this.$nextTick(function() {
                                this.$store.commit('setIsDirty', false);
                            });
                            TELSA.loading(false);
                        });
                    }
                });
            });
        },

        siteBookClick: function() {
            TELSA.loading(true);
            let route = `${TELSA.apiHost}sitebook/tree`;
            return this.$http.post(route, {args: {site: this.rightClickSelectedNodeSite}}).then(response => {
                let result = response.body;
                if(result) {
                    // There exists a sitebook so lets do some magic!
                    result.site = this.rightClickSelectedNodeSite;
                    this.selectedSiteSitebook = result;
                    this.showSitebook = true;
                }
                else {
                    this.$store.commit('showConfirmModal', {
                        title: 'Site Book Help',
                        message: `There is no site book set up for this selected site. You can create a site book from the <b>Site Book</b> page`,
                        type: '',
                        hideCancel: true,
                        confirmAction: () => {}
                    });
                }
                TELSA.loading(false);
            });
        },

        cancelEditSite: function() {
            this.$store.dispatch('handleRemoveLocks', 'edit_site');
            this.$store.dispatch('handleRemoveLocks', 'edit_sitebook');
            this.showEditSite = false;
            this.editSiteNodeSite = null;
            this.$store.state.isDirty = this.isDirtyTemp;
        },

        saveEditSite: function() {
            let route = `${TELSA.apiHost}sites/edit`;
            //let lock = this.$store.state.secondaryLocks.find(l => l.lock_name == 'edit_site') || this.$store.state.lock;
            let lock = this.$store.getters.localLock('edit_site');
            TELSA.loading(true);
            return this.$http.post(route, {args: {lock: lock, site: this.editSiteNodeSite}}).then(response => {
                let result = response.body;
                // NEED TO HANDLE RELOADING THE SITE (BOTH NODE AND IN SITE LIST)!!!!!
                let node = this.nodes.find(n => n.site_id == this.editSiteNodeSite.site_id);
                let site = this.sitesList.find(s => s.site_id == this.editSiteNodeSite.site_id);
                for(const [key, value] of Object.entries(this.editSiteNodeSite)) {
                    for(let [siteKey, siteValue] of Object.entries(node)) {
                        if(key == siteKey) {
                            site[siteKey] = value;
                            break;
                        }
                    }
                }
                let newNetworkCategory = this.siteResources.network_categories.find(nc => nc.network_category_id == site.network_category_id);
                if(newNetworkCategory) {
                    site.network_category_name = newNetworkCategory.network_category_name;
                    site.network_category_icon = newNetworkCategory.network_category_icon;
                    site.network_category_color = newNetworkCategory.network_category_color;
                    site.image = IconImageFuncs.buildIconImage(site.network_category_icon, site.network_category_color, "");
                }
                site.label = site.site_name;

                let dNode = this.nodeDataSet.get(node.id);
                this.nodeDataSet.update(dNode);

                TELSA.loading(false);
                this.cancelEditSite();
            });
        },


        // These functions are executed upon the mounting of the network map component. 
        // Theyre used to create "datasets" which are more powerful than simple arrays
        // becase they let us mutate nodes styles (eg. node backgrounds for highlightning filters)
        getNodeDataSet: function(nodeDataSet) {
            this.nodeDataSet = nodeDataSet;
        },
        getEdgeDataSet: function(edgeDataSet) {
            this.edgeDataSet = edgeDataSet;
        },


        // Functions to handle the right-click drag select box for selecting nodes and edges
        setupDragSelector: function() {
            let container = document.getElementsByClassName('vis-network')[0];
            this.makeMeMultiSelect(container, this.visJsNetwork, this.nodes);
        },

        canvasify: function(DOMx, DOMy) {
            const { x, y } = this.visJsNetwork.domToCanvas({ x: DOMx, y: DOMy });
            return [x, y];
        },

        correctRange: function(start, end) {
            return start < end ? [start, end] : [end, start];
        },

        selectFromDOMRect: function() {
            const [sX, sY] = this.canvasify(this.DOMRect.startX, this.DOMRect.startY);
            const [eX, eY] = this.canvasify(this.DOMRect.endX, this.DOMRect.endY);
            const [startX, endX] = this.correctRange(sX, eX);
            const [startY, endY] = this.correctRange(sY, eY);

            let self = this;
            let clusterNodes = this.listOfClusterNodes.filter(x => self.visJsNetwork.isCluster(x.id));

            //this.visJsNetwork.selectNodes(this.nodeDataSet.get().reduce(
            this.visJsNetwork.selectNodes((this.nodeDataSet.get().concat(clusterNodes)).reduce(
                (selected, { id }) => {
                    const { x, y } = this.visJsNetwork.getPositions(id)[id];
                    return (startX <= x && x <= endX && startY <= y && y <= endY && (!this.nodeDataSet.get(id) || !this.nodeDataSet.get(id).hidden)) ? selected.concat(id) : selected;
                }, []
            ));
            this.calculateSelectedNodes();
        },

        rectangle_mousedown: function(evt) {
            // Handle mouse down event = beginning of the rectangle selection
            var pageX = evt.pageX;    // Get the horizontal coordinate
            var pageY = evt.pageY;    // Get the vertical coordinate
            var which = evt.which;    // Get the button type

            // Check if they right-clicked on a node. If so, then dont draw a box.
            let node = this.visJsNetwork.getNodeAt({x: evt.offsetX, y: evt.offsetY});

            // When mousedown, save the initial rectangle state
            if(which === this.RIGHT_CLICK && !node) {
                this.visJsNetwork.network.canvas.body.container.style.cursor = 'crosshair';
                Object.assign(this.DOMRect, {
                    startX: pageX - this.container.getBoundingClientRect().left,
                    startY: pageY - this.container.getBoundingClientRect().top,
                    endX: pageX - this.container.getBoundingClientRect().left,
                    endY: pageY - this.container.getBoundingClientRect().top
                });
                this.drag = true;
            }
        },

        rectangle_mousedrag: function(evt) {
            // Handle mouse drag event = during the rectangle selection
            var pageX = evt.pageX;    // Get the horizontal coordinate
            var pageY = evt.pageY;    // Get the vertical coordinate
            var which = evt.which;    // Get the button type

            if(which === this.NO_CLICK && this.drag) {
                // Make selection rectangle disappear when accidently mouseupped outside 'container'
                this.drag = false;
                //network.redraw();
                this.visJsNetwork.redraw();
            } else if(this.drag) {
                // When mousemove, update the rectangle state
                Object.assign(this.DOMRect, {
                    endX: pageX - this.container.getBoundingClientRect().left,
                    endY: pageY - this.container.getBoundingClientRect().top
                });
                //network.redraw();
                this.visJsNetwork.redraw();
            }
        },

        rectangle_mouseup: function(evt) {
            // Handle mouse up event = beginning of the rectangle selection
            var pageX = evt.pageX;    // Get the horizontal coordinate
            var pageY = evt.pageY;    // Get the vertical coordinate
            var which = evt.which;    // Get the button type

            // When mouseup, select the nodes in the rectangle
            if(which === this.RIGHT_CLICK) {
                this.visJsNetwork.network.canvas.body.container.style.cursor = 'move';
                this.drag = false;
                this.visJsNetwork.redraw();
                this.selectFromDOMRect();
            }
        },

        draw_rectangle_on_network: function(ctx) {
            // Draw a rectangle regarding the current selection
            if(this.drag) {
                const [startX, startY] = this.canvasify(this.DOMRect.startX, this.DOMRect.startY);
                const [endX, endY] = this.canvasify(this.DOMRect.endX, this.DOMRect.endY);

                ctx.setLineDash([5]);
                ctx.strokeStyle = 'rgba(78, 146, 237, 0.75)';
                ctx.strokeRect(startX, startY, endX - startX, endY - startY);
                ctx.setLineDash([]);
                ctx.fillStyle = 'rgba(151, 194, 252, 0.45)';
                ctx.fillRect(startX, startY, endX - startX, endY - startY);
            }
        },


        makeMeMultiSelect: function(container, network, nodes) {
            // State
            this.drag = false;
            this.DOMRect = {};
            this.container = container;

            // Disable default right-click dropdown menu
            //container.oncontextmenu = () => false;

            // Listeners
            let self = this;
            container.addEventListener("mousedown", function(evt) { self.rectangle_mousedown(evt) });
            container.addEventListener("mousemove", function(evt) { self.rectangle_mousedrag(evt) });
            container.addEventListener("mouseup", function(evt) { self.rectangle_mouseup(evt) });
        },

        
        // Listener Functions
        keyDownListener: function(keyEvent) {
            let self = this;
            // Check that they have the network map focused. We dont want to handle keyboard inputs if its not related to network map
            if(keyEvent.target != document.getElementsByClassName('vis-network')[0]) {
                return;
            }

            // (CMD or Ctrl) + shortcuts
            if(self.cmdKeyPressed || keyEvent.ctrlKey) {
                if(keyEvent.key.toLowerCase() == "s" && (navigator.platform.match("Mac") ? keyEvent.metaKey : keyEvent.ctrlKey)) {
                    keyEvent.preventDefault();
                    if(!(self.$props.lockSave || (self.nodes.length > self.allowedNodes) || self.$props.isSaving)) {
                        self.saveNetworkMapClick();
                    }
                    return;
                }
                else if(keyEvent.key.toLowerCase() == "z" && keyEvent.shiftKey && (navigator.platform.match("Mac") ? keyEvent.metaKey : keyEvent.ctrlKey)) {
                    keyEvent.preventDefault();
                    if(self.redoStack.length) {
                        self.redoAction();
                    }
                    return;
                }
                else if(keyEvent.key.toLowerCase() == "z" && (navigator.platform.match("Mac") ? keyEvent.metaKey : keyEvent.ctrlKey)) {
                    keyEvent.preventDefault();
                    if(self.actionHistoryStack.length) {
                        self.undoAction();
                    }
                    return;
                }
            }

            let movement = keyEvent.shiftKey ? self.keyboardMoveRateShift : self.keyboardMoveRate;
            if(keyEvent.key == 'w' || keyEvent.key == 'W' || keyEvent.key == 'ArrowUp') {
                self.moveCanvas(0, (-1 * movement));
            }
            else if(keyEvent.key == 'd' || keyEvent.key == 'D' || keyEvent.key == 'ArrowRight') {
                self.moveCanvas(movement, 0);
            }
            else if(keyEvent.key == 's' || keyEvent.key == 'S' || keyEvent.key == 'ArrowDown') {
                self.moveCanvas(0, movement);
            }
            else if(keyEvent.key == 'a' || keyEvent.key == 'A' || keyEvent.key == 'ArrowLeft') {
                self.moveCanvas((-1 * movement), 0);
            }
            if(keyEvent.shiftKey) {
                self.createEdgeMode = true;
                self.createEdgeModeSelectedNodes = self.visJsNetwork.getSelectedNodes();
            }
            if(keyEvent.key.toLowerCase() == "delete") {
                self.deleteSelected();
            }

            if(keyEvent.key.toLowerCase() == "meta") {
                self.cmdKeyPressed = true;
            }
        },

        keyUpListener: function(keyEvent) {
            let self = this;
            if(!keyEvent.shiftKey) {
                self.createEdgeMode = false;
                self.createEdgeModeSelectedNodes = [];
            }
            if(keyEvent.key.toLowerCase() == "meta") {
                self.cmdKeyPressed = false;
            }
        },

        // Function to handle keyboard commands to move canvas
        moveCanvas: function(moveX, moveY) {
            let currentPosition = this.visJsNetwork.getViewPosition();
            let currentZoom = this.visJsNetwork.getScale();
            let newPosition = {x: (currentPosition.x + moveX), y: (currentPosition.y + moveY)};
            let options = {
                position: newPosition,
                scale: currentZoom,
                offset: {x: 0, y: 0},
                animation: false
            }
            this.visJsNetwork.moveTo(options);
        },

        // Functions to handle zoom in/out/fullscreen
        zoomInOut: function(change) {
            let currentPosition = this.visJsNetwork.getViewPosition();
            let currentZoom = this.visJsNetwork.getScale();
            let options = {
                position: currentPosition,
                scale: (currentZoom + change),
                offset: {x: 0, y: 0},
                animation: false
            }
            this.visJsNetwork.moveTo(options);
        },

        toggleFullscreen: function() {
            if(!document.fullscreenElement) {
                try {
                    document.getElementsByClassName('content-container')[0].requestFullscreen();
                }
                catch(error) {
                    // Must be using safari
                    document.getElementsByClassName('content-container').webkitRequestFullscreen();
                }
                this.visJsNetwork.options.height = 'calc(100vh - 0px)';
                this.fullscreenMode = true;
            }
            if(document.fullscreenElement) {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) { /* Safari */
                    document.webkitExitFullscreen();
                }
                this.visJsNetwork.options.height = 'calc(100vh - 78px)';
                this.fullscreenMode = false;
            }
        },

        initializeSetup: function() {
            // Mounted should just call this to start everything up.
            this.getSitesList();
            this.visJsNetwork = this.$refs.network;
            this.visJsNetwork.network.canvas.body.container.style.cursor = 'move';
            document.addEventListener("keydown", this.keyDownListener);
            document.addEventListener ("keyup", this.keyUpListener);
            this.setupDragSelector();
        },

        networkMapStateChange: function(action) {
            if(!this.$props.lockSave) {
                this.$store.commit('setIsDirty', true);
            }
            if(action) {
                this.actionHistoryStack.push(action);
                this.redoStack = [];
            }
            this.calculateSelectedNodes();
        },

        undoAction: function() {
            let action = this.actionHistoryStack.pop();
            if(action.type == 'addSites') {
                // to undo this we need to delete these nodes(also their associated edges?)
                let nodeIds = [], edgeIds = [];
                for(let node of action.data) {
                    nodeIds.push(node.id);
                }
                this.deleteStuff(nodeIds, edgeIds);
            }
            else if(action.type == 'addEdges') {
                let nodeIds = [], edgeIds = [];
                for(let edge of action.data) {
                    edgeIds.push(this.edges.find(e=> e.from == edge.from && e.to == edge.to).id);
                }
                this.deleteStuff(nodeIds, edgeIds);
            }
            else if(action.type == 'dragNodes') {
                for(let oldNodePosition of action.data) {
                    let node;
                    let isCluster = this.visJsNetwork.isCluster(oldNodePosition.nodeId);
                    if(isCluster) {
                        node = this.listOfClusterNodes.find(x => x.id == oldNodePosition.nodeId);
                    }
                    else {
                        node = this.nodes.find(n => n.id == oldNodePosition.nodeId);
                    }
                    let redoX = node.x;
                    let redoY = node.y;
                    node.x = oldNodePosition.x;
                    node.y = oldNodePosition.y;
                    oldNodePosition.x = redoX;
                    oldNodePosition.y = redoY;
                    if(isCluster) {
                        this.visJsNetwork.updateClusteredNode(node.id, {x: node.x, y: node.y});
                    }
                    else {
                        this.nodeDataSet.update(node);
                    }
                }
            }
            else if(action.type == 'deleteSelected') {
                let nodes = action.data.nodes;
                let edges = action.data.edges;
                for(let node of nodes) {
                    this.nodes.push(node);
                }
                for(let edge of edges) {
                    this.edges.push(edge);
                }
            }
            else if(action.type == 'clusteredNodes') {
                this.declusterNodes();
            }
            this.redoStack.push(action);
        },

        redoAction: function() {
            let action = this.redoStack.pop();
            if(action.type == 'addSites') {
                for(let node of action.data) {
                    this.nodes.push(node);
                }
            }
            else if(action.type == 'addEdges') {
                for(let edge of action.data) {
                    this.edges.push(edge);
                }
            }
            else if(action.type == 'dragNodes') {
                for(let oldNodePosition of action.data) {
                    let node;
                    let isCluster = this.visJsNetwork.isCluster(oldNodePosition.nodeId);
                    if(isCluster) {
                        node = this.listOfClusterNodes.find(x => x.id == oldNodePosition.nodeId);
                    }
                    else {
                        node = this.nodes.find(n => n.id == oldNodePosition.nodeId);
                    }
                    let redoX = node.x;
                    let redoY = node.y;
                    node.x = oldNodePosition.x;
                    node.y = oldNodePosition.y;
                    oldNodePosition.x = redoX;
                    oldNodePosition.y = redoY;
                    if(isCluster) {
                        this.visJsNetwork.updateClusteredNode(node.id, {x: node.x, y: node.y});
                    }
                    else {
                        this.nodeDataSet.update(node);
                    }
                }
            }
            else if(action.type == 'deleteSelected') {
                let nodeIds = action.data.nodes.map(n => n.id);
                let edgeIds = action.data.edges.map(e => e.id);
                this.deleteStuff(nodeIds, edgeIds);
            }
            else if(action.type == 'clusteredNodes') {
                this.clusterNodes(action.data);
            }
            this.actionHistoryStack.push(action);
        }

    },


    created() {
        //this.$vuetify.theme.dark = false;
        this.alarmColors = this.$store.state.batchConfig.webapp_settings.alarms.alarmColors.geographical;
    },

    mounted() {
        let self = this;

        // Don't get a lock if this is a new network map or if they don't have permissions to save. 
        if(this.$props.networkMap.network_map_id == 0 || this.$props.lockSave) {
            this.initializeSetup();
            return;
        }
        let lockName = `edit_network_map_${this.$props.networkMap.network_map_id}`;
        this.$store.dispatch('getLock', lockName).then(lockOwner => {
            if(lockOwner) {
                // Disable save and let user know page is locked!
                this.$props.lockSave = true;
                //this.$emit('locked');
                TELSA.loading(false);
                this.$store.commit('showConfirmModal', {
                    title: 'Lock Warning',
                    message: `<b>${this.$props.networkMap.network_map_name}</b> is already locked for editing by <font color="#FB8C00"><b>${lockOwner}</b></font>. You cannot save any changes at this time.`,
                    type: 'warning',
                    hideCancel: true,
                    confirmAction: function(){self.initializeSetup();}
                });
            }
            else {
                this.initializeSetup();
            }
        });




        
    },

    destroyed() {
        document.removeEventListener("keydown", this.keyDownListener);
        document.removeEventListener("keyup", this.keyUpListener);
        // Dont think I need this because these event listeners will get nuked when the container element gets nuked. 
        //this.container.removeEventListener("mousedown", this.rectangle_mousedown);
        clearInterval(this.alarmsIntervalFunc);
        this.visJsNetwork.destroy();
    },

    watch: {
        selectedSites: {
            handler(newVal, oldVal) {
                if(newVal.length) {
                    this.visJsNetwork.network.canvas.body.container.style.cursor = 'copy';
                }
                else {
                    this.visJsNetwork.network.canvas.body.container.style.cursor = 'move';
                }
            },
            deep: true
        },

        nodes: {
            handler(newVal, oldVal) {
                if(newVal.length > this.allowedNodes) {
                    this.$store.commit('showConfirmModal', {
                        title: 'License Limit',
                        message: `Your current network map license allows for a max of <b>${this.allowedNodes}</b> nodes. Saving will be disabled. \nPlease contact <a href="mailto:support@telsasoft.com" style="color: #c42111;">support</a> for more information.`,
                        type: 'confirm',
                        hideCancel: true,
                        confirmAction: () => {}
                    });
                }
            },
            deep: true
        },

        selectedSiteGroups: {
            handler(newVal, oldVal) {
                if(this.filtersMode) {
                    this.applyAlarmFilterStylingToNodes();
                }
            },
            deep: true
        },

        selectedSiteTagCustomGroups: {
            handler(newVal, oldVal) {
                this.applyAlarmFilterStylingToNodes();
            }
        }
    }

};
</script>


<style scoped>

    .content-container {
        width: 100%;
        height: 100%;
        position: relative;
        background-color: #fff;
    }

    .sitemap-toolbar {
        position: absolute;
        left: 0px;
        top: 0px;
        z-index: 4;
        width: initial;
        transition: all 0.3s;
        width: 700px;
    }
    .sitemap-toolbar.expand {
        /* box-shadow: none; */
    }

    .node-search-item {
        background-color: #ffffff !important;
        color: rgba(0, 0, 0, 0.87) !important;
    }

    .node-search-item .v-list-item.v-list-item--link.theme--dark {
        background-color: #ffffff !important;
        color: rgba(0, 0, 0, 0.87) !important;
    }

    .tab-content-container {
        position: absolute;
        display: block;
        background-color: #F3F1F3;
        height: 0px;
        margin-top: 48px;
        width: 700px;
        z-index: 5;
        overflow-y: auto;
        transition: all 0.3s;
        /* box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%); */
        box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 10px 10px 0px rgb(0 0 0 / 12%);
    }
    .tab-content-container.expand {
        height: calc(100% - 48px);
        transition: all 0.3s;
    }

    .special-mode-container {
        position: absolute;
        text-align: center;
        top: 5px;
        color: #c42111;
        font-size: 18px;
        left: calc(50% - (343px / 2));
        background-color: #F3F1F3;
        border: black 1px solid;
        padding: 5px 10px 5px 10px;
        border-radius: 10px;
        box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%);
        z-index: 5;
    }

    .alarm-actions-container {
        position: absolute;
        right: 5px;
        top: 5px;
        z-index: 5;
        width: 40px;
        height: 40px;
        background-color: #FFFFFF;
        border: #000 solid 1px;
        box-shadow: 0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%);
        transition: all 0.3s;
        text-align: center;
        border-radius: 7px;
    }
    .alarm-actions-container.expanded {
        width: 250px;
        height: 150px;
        transition: all 0.3s;
    }
    .alarm-actions-container .alarm-actions-icon {
        color: #c42111;
        cursor: pointer;
        /* float: right; */
    }
    .alarm-actions-container .content {
        text-align: center;
        position: relative;
        display: block;
        width: 100%;
        
    }
    .alarm-actions-container .content .close {
        position: absolute;
        top: -32px;
        right: 3px;
    }

    .network-container {
        display: block;
        position: relative;
        width: 100%;
        height: 100%;
        z-index: 1;

        background: radial-gradient(#F2F4F4 15%, transparent 16%) 0 0,
        radial-gradient(#F2F4F4 15%, transparent 16%) 4px 4px,
        radial-gradient(rgba(255,255,255,.8) 15%, transparent 20%) 0 1px,
        radial-gradient(rgba(255,255,255,.8) 15%, transparent 20%) 8px 9px;
        background-color:#FFFFFF;
        background-size:8px 8px;
    }

    .slide-out-site-list {
        width: 100%;
        position: relative;
        z-index: 5;
        
    }
    .slide-out-site-list .site-list-table {
        position: relative;

    }
    .slide-out-site-list .site-row {
        cursor: pointer;
    }
    .slide-out-site-list .site-row.selected {
        background-color: lightblue;
        color: #000000;
    }
    .slide-out-site-list .site-row.disabled {
        cursor: initial;
        background-color: lightgray;
        color: gray;
    }

    .slide-out-settings {
        position: relative;
        z-index: 5;
        background-color: #F3F1F3;
    }

    .alarm-filter-severity {
        display: inline-block;
        width: calc(100% / 4);
    }
    .settings-label {
        color: #000000;
        opacity: 0.5;
    }

    .zoom-buttons {
        position: absolute;
        z-index: 6;
        border: black solid 1px !important;
    }
    .zoom-buttons.in {
        bottom: 60px;
        right: 10px;
    }
    .zoom-buttons.out {
        bottom: 10px;
        right: 10px;
    }
    .zoom-buttons.fullscreen {
        bottom: 10px;
        right: 60px;
    }

    .cluster-container {
        display: block;
        height: 0px;
        background-color: #F3F1F3;
        /* width: 333px; */
        width: 409px;
        position: absolute;
        top: 58px;
        left: 311px;
        border: none;
        text-align: center;
        transition: 0.1s all;
    }
    .cluster-container.show {
        height: 96px;
        border-left: gray solid 1px;
        border-right: gray solid 1px;
        border-bottom: gray solid 1px;
        border-top: gray solid 1px;
        box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 10px 10px 0px rgb(0 0 0 / 12%);
        transition: 0.3s all;
    }

    .cluster-container.show .up-arrow{
        width: 0; 
        height: 0; 
        border-left: 20px solid transparent;
        border-right: 20px solid transparent;
        border-bottom: 20px solid gray;
        text-align: center;
        position: absolute;
        left: calc(50% - (20px / 2));
        top: -20px;
    }
    .cluster-container.show .up-arrow .inner {
        position: relative;
        top: 0px;
        left: -19px;
        width: 0;
        height: 0;
        border-left: 19px solid transparent;
        border-right: 19px solid transparent;
        border-bottom: 20px solid #F3F1F3;
    }

    .cluster-container.show .cluster-method {
        display: inline-block;
        position: relative;
        cursor: pointer;
        text-align: center;
        padding: 15px 15px 0px 15px;
        padding-top: 15px;
        height: 100%;
    }
    .cluster-container.show .cluster-method:hover {
        background-color: darkgray;
    }
    .cluster-container.show .cluster-method .i {
        display: block;
    }
    .cluster-container.show .cluster-method span {
        display: block;
        font-size: 10px;
    }

</style>

<style>

    .vis-active:focus {
        outline: none !important;
        box-shadow: unset !important;
    }

    .vis-active {
        outline: none !important;
        box-shadow: unset !important;
    }

    .edit-site-container {
        background-color: #242424;
        padding: 20px;
        
    }

</style>


